import {
  MutationCache,
  type MutationMeta,
  noop,
  type Query,
  QueryCache,
  QueryClient,
  type QueryClientConfig,
  type QueryMeta,
} from '@tanstack/react-query';
import type { KyInstance } from 'ky';

/**
 * @description `gcTime` - The time in milliseconds that unused/inactive cache data remains in memory.
 * When a query's cache becomes unused or inactive, that cache data will be garbage collected after this duration.
 * Query results that have no more active instances of `useQuery`,
 * `useInfiniteQuery` or query observers are labeled as `"inactive"`.
 *
 * @link https://tanstack.com/query/latest/docs/framework/react/guides/important-defaults
 */
export const ReactQueryCacheTime = {
  MINUTES_15: 60 * 1000 * 15,
  MINUTES_90: 60 * 1000 * 90,
} as const;

/**
 * @description `staleTime` - The time in milliseconds after data is considered stale.
 * Stale queries are refetched automatically in the background when:
 * * New instances of the query mount
 * * The window is refocused (disabled in `ui-unity`)
 * * The network is reconnected
 * * The query is optionally configured with a refetch interval
 *
 * @link https://tanstack.com/query/latest/docs/framework/react/guides/important-defaults
 *
 * @default 10 seconds
 */
export const ReactQueryStaleTime = {
  /** 10 seconds */
  DEFAULT: 10 * 1000,
  MINUTES_15: 60 * 1000 * 15,
  MINUTES_60: 60 * 1000 * 60,
} as const;

export interface QueryClientOptionsParams {
  http: KyInstance;
  onQueryError?: (error: Error, query: Query<unknown, unknown, unknown>) => void;
  meta?: QueryMeta & MutationMeta;
}

export const queryClientOptions = ({
  http,
  onQueryError = noop,
  meta,
}: QueryClientOptionsParams): QueryClientConfig => ({
  defaultOptions: {
    queries: {
      meta: { ...meta, http },
      refetchOnWindowFocus: false,
      staleTime: ReactQueryStaleTime.DEFAULT,
    },
    mutations: {
      meta: { ...meta, http },
    },
  },
  queryCache: new QueryCache({
    onError: onQueryError,
  }),
  mutationCache: new MutationCache({
    onSuccess: async (_data, _variables, _onMutateResult, mutation, context) => {
      if (mutation.meta?.invalidation === 'await') {
        await context.client.invalidateQueries();
      } else if (mutation.meta?.invalidation !== 'off') {
        context.client.invalidateQueries();
      }
    },
  }),
});

export const createQueryClient = (
  http: KyInstance,
  onQueryError: (error: Error, query: Query<unknown, unknown, unknown>) => void = noop
) => {
  return new QueryClient(queryClientOptions({ http, onQueryError }));
};
